// The page an AI app sends you to when you connect it by the board's address:
// "Claude wants to use your board". Nothing here trusts the link it came in on.
// The server says who the app is and where it may send you back, the page
// never sits inside another page, and only Allow hands the app a code.

import { useEffect, useMemo, useState } from "react";
import { Check, Copy, ShieldCheck, TriangleAlert } from "lucide-react";
import { api, getToken, setToken, type ApiError } from "../lib/api";
import { copyText } from "../lib/util";
import { Backdrop } from "../themes/Backdrop";
import { resolveTheme, THEME } from "../themes/themes";
import { Logo } from "./Logo";
import { keepTicket, readTicket, SignIn, type KeptTicket } from "./Welcome";
import { signinTheme, type SigninTheme } from "./signinTheme";

interface AppInfo {
  name: string;
  verified: boolean;
  /** the domain a verified app proved it owns */
  site: string | null;
  /** where Allow sends you: a site, or an app on this computer */
  back: string;
}

export function OAuthConsent() {
  const q = useMemo(() => Object.fromEntries(new URLSearchParams(location.search)), []);
  // the sign-in page's scene of the day
  const [scene] = useState(signinTheme);
  const theme = resolveTheme({ id: scene });
  const framed = typeof window !== "undefined" && window.top !== window.self;
  const [app, setApp] = useState<AppInfo | null>(null);
  const [bad, setBad] = useState<string | null>(null);
  const [who, setWho] = useState<{ name: string; email: string | null } | null>(null);
  const [signedIn, setSignedIn] = useState(() => !!getToken());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [handedOff, setHandedOff] = useState(false);
  const [copied, setCopied] = useState(false);
  // a new email signed in here: asked before its board is made, in case it was the wrong address;
  // its ticket is kept for this tab, so a reload or a failed start keeps it
  const [fresh, setFresh] = useState<KeptTicket | null>(readTicket);
  const holdFresh = (t: KeptTicket | null) => {
    keepTicket(t);
    setFresh(t);
  };

  useEffect(() => {
    if (framed) return;
    if (q.response_type !== "code" || !q.client_id || !q.redirect_uri) {
      setBad("This link is missing what an app needs to sign in. Start again from the app.");
      return;
    }
    if (q.code_challenge_method !== "S256" || !q.code_challenge) {
      setBad("This app did not send a PKCE challenge, so the board cannot sign it in safely.");
      return;
    }
    api<AppInfo>(`/api/oauth/client?client_id=${encodeURIComponent(q.client_id)}&redirect_uri=${encodeURIComponent(q.redirect_uri)}`, { token: null }).then(setApp, (e) => setBad((e as Error).message));
  }, [q, framed]);

  useEffect(() => {
    if (!signedIn) return;
    api<{ workspace: { name: string; email: string | null } }>("/api/snapshot").then(
      (s) => setWho({ name: s.workspace.name, email: s.workspace.email }),
      (e) => {
        const status = (e as ApiError).status;
        // a token from an old session: sign in again; any other failure keeps the session and says what went wrong
        if (status === 401 || status === 404) {
          setToken(null);
          setSignedIn(false);
        } else setErr((e as Error).message);
      },
    );
  }, [signedIn]);

  const allow = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ redirect: string }>("/api/oauth/approve", {
        body: { client_id: q.client_id, redirect_uri: q.redirect_uri, state: q.state ?? "", code_challenge: q.code_challenge, code_challenge_method: q.code_challenge_method, scope: q.scope ?? "board", resource: q.resource ?? "" },
      });
      setHandedOff(true);
      location.href = r.redirect;
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  const cancel = () => {
    // only an address the server has already checked for this app
    const back = new URL(q.redirect_uri);
    back.searchParams.set("error", "access_denied");
    if (q.state) back.searchParams.set("state", q.state);
    location.href = back.toString();
  };

  // a new email, once confirmed, starts its board as Founder HQ, then allows
  const startFor = async (ticket: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ token: string }>("/api/workspaces", { body: { template: "founder", ticket }, token: null });
      setToken(r.token);
      holdFresh(null);
      setSignedIn(true);
    } catch (e) {
      // only a ticket that has run out or was used is let go; after any other failure Start a board can be tried again
      if ((e as ApiError).code === "signin_expired") holdFresh(null);
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (framed) body = <p className="consent-bad">This page cannot open inside another page. Open it in its own tab.</p>;
  else if (bad)
    body = (
      <>
        <h3>That did not work</h3>
        <p className="consent-bad">{bad}</p>
      </>
    );
  else if (!app) body = <span className="spinner" />;
  else if (handedOff)
    body = (
      <>
        <div className="consent-done">
          <Check size={22} />
        </div>
        <h3>{app.name} is connected</h3>
        <p>You can go back to {app.name} now.</p>
      </>
    );
  else if (!signedIn)
    // every board has an email now, so signing in works from whichever browser the app opened;
    // the link is for someone already signed in somewhere else
    return (
      <Frame theme={theme} scene={scene}>
        <div className="consent-signin">
          {busy ? (
            <div className="consent island">
              <span className="spinner" />
            </div>
          ) : fresh ? (
            <div className="consent island">
              <h3>No board for {fresh.email} yet</h3>
              <p>Start one to connect {app.name}? If your board is under another email, sign in with that one instead.</p>
              <div className="consent-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setErr(null);
                    holdFresh(null);
                  }}
                >
                  Use another email
                </button>
                <button className="btn btn-primary" onClick={() => void startFor(fresh.ticket)} autoFocus>
                  Start a board
                </button>
              </div>
            </div>
          ) : (
            <SignIn
              title={`Sign in to connect ${app.name}`}
              sub="Use the email your board is under. New here? The same code starts your board."
              onSignedIn={() => {
                setErr(null);
                setSignedIn(true);
              }}
              onFresh={(ticket, email) => {
                setErr(null);
                holdFresh({ ticket, email });
              }}
            />
          )}
          {err && <p className="consent-bad">{err}</p>}
          <button
            type="button"
            className="link-btn consent-copy"
            onClick={() =>
              void copyText(location.href).then((ok) => {
                setCopied(ok);
                if (!ok) setErr("Your browser did not let the page copy. Copy the address from the address bar instead.");
              })
            }
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? "Copied. Open it in the browser your board is signed in to." : "Signed in in another browser? Copy this page's link"}
          </button>
        </div>
      </Frame>
    );
  else
    body = (
      <>
        <h3>
          {app.name} wants to use your board
        </h3>
        <div className={app.verified ? "consent-who is-verified" : "consent-who"}>
          {app.verified ? <ShieldCheck size={14} /> : <TriangleAlert size={14} />}
          <span>{app.verified ? `Verified as ${app.site ?? app.back}` : `Not verified. It will send you back to ${app.back}. Only allow it if you just set it up yourself.`}</span>
        </div>
        {/* what every agent on a board key can do (handleMcp in worker/workspace.ts): said in full before anyone allows it */}
        <ul className="consent-can">
          <li>Read, add, change and remove anything on your boards</li>
          <li>Read from the apps and services you connect, including Google Analytics and Search Console, and any you add later</li>
          <li>Post updates you will see on the board</li>
        </ul>
        <p className="consent-where">
          Workspace: <b>{who?.name ?? "…"}</b>
          {who?.email ? ` (${who.email})` : ""}. You can disconnect it any time in Connect.
        </p>
        {err && <p className="consent-bad">{err}</p>}
        <div className="consent-actions">
          <button className="btn btn-ghost" onClick={cancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void allow()} disabled={busy || !who} autoFocus>
            {busy ? <span className="spinner" /> : "Allow"}
          </button>
        </div>
      </>
    );
  return <Frame theme={theme} scene={scene}>{<div className="consent island">{body}</div>}</Frame>;
}

function Frame({ theme, scene, children }: { theme: ReturnType<typeof resolveTheme>; scene: SigninTheme; children: React.ReactNode }) {
  return (
    <div className="board-root welcome consent-page" data-mode="dark" style={theme.vars as React.CSSProperties}>
      <Backdrop def={THEME[scene]} motion={false} dim={0.18} />
      <header className="welcome-top">
        <Logo />
      </header>
      <main className="consent-main">{children}</main>
    </div>
  );
}
